import React, { useRef, useEffect, useMemo, useState } from "react";
import { Canvas, useThree, useFrame } from "@react-three/fiber";
import { OrthographicCamera, ContactShadows } from "@react-three/drei";
import * as THREE from "three";
import Building from "./Building";
import Ground from "./Ground";
import Paths from "./Paths";
import Decor from "./Decor";
import Sky from "./Sky";
import Life from "./Life";
import OwlShrine, { OWL_SHRINE } from "./OwlShrine";
import { BRAND } from "../lib/brand";
import { WorldThemeContext, useNight } from "../lib/worldTheme";
import { fitZoom, focusZoom, cityCenter, findBuilding } from "./helpers";

// Track native browser fullscreen so we can hide building labels (the Owl Shrine
// label is intentionally kept visible — see Scene below). Works for the in-app
// fullscreen button, an iframe host going fullscreen, and native F11.
function useIsFullscreen() {
  const [fs, setFs] = useState(() => (typeof document !== "undefined" ? !!document.fullscreenElement : false));
  useEffect(() => {
    const onChange = () => setFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
    };
  }, []);
  return fs;
}

// Default isometric framing as spherical angles (matches the old ISO_DIR look).
const CAM_DIST = 190;
const AZ0 = Math.PI / 4;
const PO0 = Math.asin(0.5453); // ~33° elevation
// Centre the overview on the city core (raise the look-at so the skyline sits
// nicely centred rather than floating high in the frame).
const OVERVIEW_OFFSET = new THREE.Vector3(0, 3.5, 0);

function CameraController({ buildings, focusables, selected, dragRef }) {
  const { camera, size, gl } = useThree();
  const target = useRef(new THREE.Vector3(0, 2, 0));
  const goalTarget = useRef(new THREE.Vector3(0, 2, 0));
  const zoom = useRef(10);
  const goalZoom = useRef(14);
  const started = useRef(false);
  const azimuth = useRef(AZ0);
  const polar = useRef(PO0);

  useEffect(() => {
    const f = fitZoom(size);
    const c = cityCenter(buildings).add(OVERVIEW_OFFSET);
    target.current.copy(c);
    goalTarget.current.copy(c);
    if (!started.current) {
      zoom.current = f * 0.6;
      started.current = true;
    }
    goalZoom.current = f;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const b = findBuilding(focusables || buildings, selected);
    if (b) {
      const bh = Number(b.height) || 6;
      const isBeacon = b.shape === "beacon";
      // The beacon's hero feature (the spinning S/M logo) sits at the very top, so
      // centre higher and pull back a touch to keep the whole tower + logo in frame.
      const ty = isBeacon ? bh * 0.82 : bh / 2 + 1;
      goalTarget.current.set(Number(b.positionX) || 0, ty, Number(b.positionZ) || 0);
      goalZoom.current = isBeacon ? focusZoom(size) * 0.82 : focusZoom(size);
    } else {
      goalTarget.current.copy(cityCenter(buildings).add(OVERVIEW_OFFSET));
      goalZoom.current = fitZoom(size);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, buildings, size.width, size.height]);

  // Drag anywhere on the canvas to orbit (azimuth) and tilt (polar) the view.
  useEffect(() => {
    const el = gl.domElement;
    let dragging = false;
    let lx = 0;
    let ly = 0;
    const onDown = (e) => {
      dragging = true;
      lx = e.clientX;
      ly = e.clientY;
      if (dragRef) dragRef.current = 0;
    };
    const onMove = (e) => {
      if (!dragging) return;
      const dx = e.clientX - lx;
      const dy = e.clientY - ly;
      if (dragRef) dragRef.current += Math.abs(dx) + Math.abs(dy);
      azimuth.current -= dx * 0.005;
      polar.current = THREE.MathUtils.clamp(polar.current + dy * 0.0045, 0.22, 1.15);
      lx = e.clientX;
      ly = e.clientY;
    };
    const onUp = () => {
      dragging = false;
    };
    el.addEventListener("pointerdown", onDown);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      el.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [gl, dragRef]);

  useFrame((_, delta) => {
    const t = 1 - Math.pow(0.0016, Math.min(delta, 0.05));
    target.current.lerp(goalTarget.current, t);
    zoom.current = THREE.MathUtils.lerp(zoom.current, goalZoom.current, t);
    const cp = Math.cos(polar.current);
    const sp = Math.sin(polar.current);
    const sa = Math.sin(azimuth.current);
    const ca = Math.cos(azimuth.current);
    camera.position.set(
      target.current.x + CAM_DIST * cp * sa,
      target.current.y + CAM_DIST * sp,
      target.current.z + CAM_DIST * cp * ca
    );
    camera.up.set(0, 1, 0);
    camera.lookAt(target.current);
    camera.zoom = zoom.current;
    camera.updateProjectionMatrix();
  });

  return null;
}

function Lights() {
  const night = useNight();
  if (night) {
    return (
      <group>
        <hemisphereLight args={["#243049", "#0b0e16", 0.55]} />
        <ambientLight intensity={0.22} color="#8fa6d8" />
        <directionalLight
          position={[-26, 40, -8]}
          intensity={0.6}
          color="#aebfe6"
          castShadow
          shadow-mapSize-width={2048}
          shadow-mapSize-height={2048}
          shadow-camera-left={-48}
          shadow-camera-right={48}
          shadow-camera-top={48}
          shadow-camera-bottom={-48}
          shadow-camera-near={0.5}
          shadow-camera-far={220}
          shadow-bias={-0.0004}
        />
      </group>
    );
  }
  return (
    <group>
      <hemisphereLight args={["#ffffff", BRAND.surface2, 0.85]} />
      <ambientLight intensity={0.35} />
      <directionalLight
        position={[34, 46, 20]}
        intensity={1.7}
        castShadow
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
        shadow-camera-left={-50}
        shadow-camera-right={50}
        shadow-camera-top={50}
        shadow-camera-bottom={-50}
        shadow-camera-near={0.5}
        shadow-camera-far={240}
        shadow-bias={-0.0004}
      />
      <directionalLight position={[-28, 18, -14]} intensity={0.3} />
    </group>
  );
}

function Scene({ buildings, selected, onSelect, onPersonSelect, showLabels = true }) {
  const [hovered, setHovered] = useState(null);
  const dragRef = useRef(0);
  const isFullscreen = useIsFullscreen();
  // Owl shrine is focusable like a building but never enters the buildings list.
  const focusables = useMemo(() => [...buildings, OWL_SHRINE], [buildings]);
  // Suppress selection if the pointer was dragged (i.e. the user was rotating).
  const handleSelect = (slug) => {
    if (dragRef.current > 7) return;
    onSelect(slug);
  };
  // Clicking a citizen opens their persona panel (also drag-guarded).
  const handlePersonSelect = (persona) => {
    if (dragRef.current > 7) return;
    if (onPersonSelect) onPersonSelect(persona);
  };
  // In fullscreen, hide ALL building labels (the Owl Shrine label stays — it owns
  // its own <Html> in OwlShrine.js and is intentionally never gated here).
  const buildingLabels = showLabels && !isFullscreen;
  return (
    <group>
      <CameraController buildings={buildings} focusables={focusables} selected={selected} dragRef={dragRef} />
      <Lights />
      <Ground buildings={buildings} />
      <Paths buildings={buildings} />
      <Decor buildings={buildings} />
      <Life buildings={buildings} onPersonSelect={handlePersonSelect} />
      <OwlShrine selected={selected} onSelect={handleSelect} />
      <Sky />

      <mesh
        rotation={[-Math.PI / 2, 0, 0]}
        position={[0, 0.0, 0]}
        onClick={(e) => {
          e.stopPropagation();
          handleSelect(null);
        }}
      >
        <planeGeometry args={[100, 100]} />
        <meshBasicMaterial transparent opacity={0} />
      </mesh>

      {buildings.map((b) => (
        <Building
          key={b.id || b.slug}
          b={b}
          isActive={selected === b.slug || selected === b.id}
          isHovered={hovered === b.slug}
          dimmed={!!selected && selected !== b.slug && selected !== b.id}
          onSelect={handleSelect}
          onHover={setHovered}
          showLabels={buildingLabels}
        />
      ))}

      <ContactShadows position={[0, 0.05, 0]} opacity={0.4} scale={86} blur={2.6} far={30} resolution={1024} />
    </group>
  );
}

export default function IsoWorld({ buildings, selected, onSelect, onPersonSelect, night = false, showLabels = true, className }) {
  const bg = night ? "#0E121C" : BRAND.bg;
  return (
    <div className={className} style={{ position: "absolute", inset: 0, background: bg }}>
      <Canvas
        shadows
        frameloop="always"
        dpr={[1, 1.9]}
        gl={{ antialias: true, powerPreference: "high-performance" }}
        style={{ width: "100%", height: "100%" }}
      >
        <color attach="background" args={[bg]} />
        <OrthographicCamera makeDefault position={[120, 110, 120]} zoom={12} near={-400} far={900} />
        <WorldThemeContext.Provider value={night}>
          <Scene buildings={buildings} selected={selected} onSelect={onSelect} onPersonSelect={onPersonSelect} showLabels={showLabels} />
        </WorldThemeContext.Provider>
      </Canvas>
    </div>
  );
}
